import React, { useState } from "react";
import { motion } from "framer-motion";
import { researchDomains } from "../mock";
import DomainDetailModal from "./DomainDetailModal";
import SectionHeader from "./SectionHeader";
import {
  Activity,
  HeartPulse,
  BarChart3,
  BookOpen,
  Brain,
  Users,
  ArrowUpRight,
} from "lucide-react";

const iconMap = {
  Activity,
  HeartPulse,
  BarChart3,
  BookOpen,
  Brain,
  Users,
};

const TeamSection = () => {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openDomain = (domain) => {
    setSelectedDomain(domain);
    setIsOpen(true);
  };

  return (
    <section
      id="team"
      data-testid="research-domains-section"
      className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#FAEDE7] overflow-hidden"
    >
      {/* subtle background texture */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #000 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          eyebrow="Research Domains"
          title="Six interdisciplinary frontiers shaping the"
          titleAccent="Future of Human-Aligned AI"
          subtitle="Our center pursues a deeply interdisciplinary agenda — fusing biomedicine, cognition, data, and the humanities to build AI that is scientifically rigorous and humanly meaningful."
          animateWords
          index={1}
          className="mb-16 sm:mb-20"
        />

        {/* Domain grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {researchDomains.map((domain, idx) => {
            const Icon = iconMap[domain.icon] || Activity;
            return (
              <article
                key={domain.id}
                data-testid={`domain-card-${domain.code.toLowerCase()}`}
                onClick={() => openDomain(domain)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openDomain(domain);
                  }
                }}
                role="button"
                tabIndex={0}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-gray-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 p-7 sm:p-8 min-h-[340px] flex flex-col"
                style={{ "--accent": domain.accent }}
              >
                {/* Watermark number */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-4 -top-10 text-[180px] font-extralight leading-none select-none transition-opacity duration-500 group-hover:opacity-[0.10]"
                  style={{
                    color: domain.accent,
                    opacity: 0.06,
                  }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </div>

                {/* Top meta row */}
                <div className="relative flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="px-2.5 py-1 text-[10px] font-mono tracking-[0.25em] uppercase text-white rounded-sm"
                      style={{ background: domain.accent }}
                    >
                      {domain.code}
                    </span>
                    <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-gray-400">
                      / 0{idx + 1}
                    </span>
                  </div>

                  {/* Small stamp photo */}
                  <motion.div
                    layoutId={`stamp-${domain.code}`}
                    className="relative w-[72px] h-[72px] rounded-lg overflow-hidden flex-shrink-0 shadow-sm"
                    style={{ border: `1px solid ${domain.accent}33` }}
                    transition={{ type: "spring", stiffness: 260, damping: 30 }}
                  >
                    <img
                      src={domain.image}
                      alt={domain.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(180deg, transparent 50%, ${domain.accent}88)`,
                      }}
                    />
                    <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center">
                      <Icon
                        className="w-3 h-3"
                        style={{ color: domain.accent }}
                      />
                    </div>
                    {/* passport ticks */}
                    <div
                      className="absolute top-1 left-1 w-2 h-2 border-l border-t"
                      style={{ borderColor: "#ffffffaa" }}
                    />
                    <div
                      className="absolute top-1 right-1 w-2 h-2 border-r border-t"
                      style={{ borderColor: "#ffffffaa" }}
                    />
                  </motion.div>
                </div>

                {/* Title block */}
                <div className="relative mt-6 flex-1">
                  <h3 className="text-[28px] sm:text-[30px] font-light text-gray-900 tracking-tight leading-[1.05]">
                    {domain.title}
                  </h3>
                  <p
                    className="mt-2 text-xs font-medium tracking-[0.05em]"
                    style={{ color: domain.accent }}
                  >
                    {domain.tagline}
                  </p>
                  <p className="mt-5 text-sm text-gray-600 font-light leading-relaxed line-clamp-3">
                    {domain.description}
                  </p>
                </div>

                {/* Footer */}
                <div className="relative mt-6 pt-4 border-t border-dashed border-gray-200 flex items-center justify-between">
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.25em] transition-all duration-300"
                    style={{ color: domain.accent }}
                  >
                    Explore domain
                  </span>
                  <span className="flex items-center gap-1 text-gray-300 group-hover:text-gray-900 transition-colors">
                    <span
                      className="h-px w-6 transition-all duration-500 group-hover:w-12"
                      style={{ background: domain.accent }}
                    />
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <DomainDetailModal
        domain={selectedDomain}
        open={isOpen}
        onOpenChange={setIsOpen}
      />
    </section>
  );
};

export default TeamSection;
